import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { destinations, categories } from '../mock';
import ActivityCard from '../components/ActivityCard';
import ActivityListCard from '../components/ActivityListCard';
import Pagination from '../components/Pagination';
import { Filter, LayoutGrid, List, Search } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useActivities } from '../hooks/use-activities';
import { useT } from '../i18n/LanguageContext';
import { useLocalizedActivities, useLocalizedDestinations } from '../i18n/contentLocale';

const PAGE_SIZES = [9, 18, 36, 50];

export default function Activities() {
  const [params] = useSearchParams();
  const { currency, format: formatPrice } = useCurrency();
  const t = useT();
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [price, setPrice] = useState([0, 1200]);          // live thumbs
  const [appliedPrice, setAppliedPrice] = useState([0, 1200]); // committed → query
  const [sort, setSort] = useState('featured');
  const [view, setView] = useState('list');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  // Server-side query: filters + sort + pagination all run in the backend.
  const query = useMemo(() => {
    const p = { limit: pageSize, skip: (page - 1) * pageSize, sort };
    if (qDebounced) p.q = qDebounced;
    if (selectedDestinations.length) p.island = selectedDestinations.join(',');
    if (selectedCategories.length) p.category = selectedCategories.join(',');
    if (appliedPrice[0] > 0) p.min_price = appliedPrice[0];
    if (appliedPrice[1] < 1200) p.max_price = appliedPrice[1];
    return p;
  }, [pageSize, page, sort, qDebounced, selectedDestinations, selectedCategories, appliedPrice]);

  const { data, isLoading, isError, isFetching } = useActivities(query);
  const rawActivities = useMemo(() => data?.items || [], [data]);
  const activities = useLocalizedActivities(rawActivities);
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const localizedDestinations = useLocalizedDestinations(destinations);

  useEffect(() => {
    const d = params.get('destination');
    if (d) setSelectedDestinations([d]);
    const query = params.get('q');
    if (query) setQ(query);
  }, [params]);

  // Any filter/sort/size change sends us back to page 1.
  useEffect(() => {
    setPage(1);
  }, [qDebounced, selectedDestinations, selectedCategories, appliedPrice, sort, pageSize]);

  // Jump to the top of the results whenever the page changes.
  const changePage = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  // Category names share the same taxonomy as the home "Best of" filters, which
  // are already translated — reuse those keys so the filter labels localize too.
  const categoryLabelKey = {
    nauticas: 'bestOf.filterWater',
    buceo: 'bestOf.filterDiving',
    aventura: 'bestOf.filterLand',
    aire: 'bestOf.filterAir',
    naturaleza: 'bestOf.filterNature',
    gastronomia: 'bestOf.filterFood',
  };

  return (
    <div className="pt-20 bg-white min-h-screen">
      {/* Hero */}
      <div className="relative h-[42vh] min-h-[300px] w-full flex items-center justify-center overflow-hidden">
        <img src="https://images.unsplash.com/photo-1647002408653-129115ac90e1?auto=format&fit=crop&w=2000&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0b1c26]/50" />
        <div className="relative text-center text-white px-5">
          <p className="text-[#c8a25a] text-sm font-semibold tracking-widest uppercase mb-3">{t('activitiesPage.eyebrow')}</p>
          <h1 className="text-4xl md:text-6xl font-bold">{t('activitiesPage.headingPre')}<span className="italic font-medium">{t('activitiesPage.headingEm')}</span></h1>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">{t('activitiesPage.subtitle')}</p>
        </div>
      </div>

      {/* Mobile filters toggle */}
      <div className="lg:hidden max-w-7xl mx-auto px-5 md:px-8 pt-8">
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-[#14213d] hover:border-[#1fa5a3] hover:text-[#1fa5a3]"
        >
          <Filter className="w-4 h-4" /> {showFilters ? t('activitiesPage.hide') : t('activitiesPage.filters')}
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 lg:py-12 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
        {/* Filters */}
        <aside className={`space-y-8 bg-[#f7f9fb] p-6 rounded-2xl h-fit lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-4 [scrollbar-width:thin] ${showFilters ? 'block' : 'hidden'} lg:block`}>
          <div>
            <div className="flex items-center gap-2 font-semibold text-[#14213d] mb-4">
              <Filter className="w-4 h-4" /> {t('activitiesPage.filters')}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('activitiesPage.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-[#1fa5a3]"
              />
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-[#14213d] mb-3 text-sm uppercase tracking-wide">{t('activitiesPage.destinations')}</h4>
            <div className="space-y-2">
              {localizedDestinations.map((d) => (
                <label key={d.id} className="flex items-center gap-2 cursor-pointer text-[15px] text-gray-700">
                  <Checkbox
                    checked={selectedDestinations.includes(d.id)}
                    onCheckedChange={() => toggle(selectedDestinations, setSelectedDestinations, d.id)}
                  />
                  <span>{d.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-[#14213d] mb-3 text-sm uppercase tracking-wide">{t('activitiesPage.categories')}</h4>
            <div className="space-y-2">
              {categories.map((c) => (
                <label key={c.id} className="flex items-center gap-2 cursor-pointer text-[15px] text-gray-700">
                  <Checkbox
                    checked={selectedCategories.includes(c.id)}
                    onCheckedChange={() => toggle(selectedCategories, setSelectedCategories, c.id)}
                  />
                  <span>{categoryLabelKey[c.id] ? t(categoryLabelKey[c.id]) : c.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-[#14213d] mb-4 text-sm uppercase tracking-wide">{t('activitiesPage.price')} ({currency})</h4>
            <Slider min={0} max={1200} step={10} value={price} onValueChange={setPrice} onValueCommit={setAppliedPrice} />
            <div className="mt-3 flex justify-between text-sm text-gray-600">
              <span>{formatPrice(price[0])}</span>
              <span>{formatPrice(price[1])}</span>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
            <p className="text-gray-600"><span className="font-semibold text-[#14213d]">{total}</span> {t('activitiesPage.resultsFound')}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <label className="text-sm text-gray-500">{t('activitiesPage.perPage')}</label>
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1fa5a3]">
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              {/* View toggle: list (default) / grid */}
              <div className="hidden sm:flex items-center rounded-lg border border-gray-200 bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setView('list')}
                  aria-label={t('activitiesPage.viewList')}
                  aria-pressed={view === 'list'}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${view === 'list' ? 'bg-[#1fa5a3] text-white' : 'text-gray-500 hover:text-[#1fa5a3]'}`}
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setView('grid')}
                  aria-label={t('activitiesPage.viewGrid')}
                  aria-pressed={view === 'grid'}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${view === 'grid' ? 'bg-[#1fa5a3] text-white' : 'text-gray-500 hover:text-[#1fa5a3]'}`}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>
              <label className="text-sm text-gray-500">{t('activitiesPage.sort')}</label>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1fa5a3]">
                <option value="featured">{t('activitiesPage.sortFeatured')}</option>
                <option value="price-asc">{t('activitiesPage.sortPriceAsc')}</option>
                <option value="price-desc">{t('activitiesPage.sortPriceDesc')}</option>
                <option value="rating">{t('activitiesPage.sortRating')}</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-gray-100 bg-gray-100 h-40 animate-pulse" />
              ))}
            </div>
          ) : isError ? (
            <div className="text-center py-20 text-gray-500">
              {t('activitiesPage.loadError')}
            </div>
          ) : activities.length ? (
            <>
              <div className={isFetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                {view === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {activities.map((a) => <ActivityCard key={a.id} activity={a} />)}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((a) => <ActivityListCard key={a.id} activity={a} />)}
                  </div>
                )}
              </div>
              <Pagination page={page} pageCount={pageCount} onPageChange={changePage} />
            </>
          ) : (
            <div className="text-center py-20 text-gray-500">{t('activitiesPage.empty')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
